/**
 * Bound a promise so it ALWAYS settles within `ms`. On timeout it rejects with a
 * TimeoutError (`isTimeout: true`) that flows into the caller's existing catch →
 * the caller's `finally` runs → a stuck `saving` flag clears and the optimistic
 * row rolls back.
 *
 * WHY (bug: frozen no-service/reschedule modal on the iOS PWA): mutation RPCs and
 * table writes have NO fetch timeout — `authTimeoutFetch` (src/supabase.js) applies
 * `AbortSignal.timeout` only to `/auth/v1/` URLs; PostgREST/RPC keep the default
 * no-timeout behaviour. When iOS suspends the PWA mid-`await supabase.rpc(...)` it
 * tears down the socket, so the promise can hang forever → the handler's
 * `finally { setIsSaving(false) }` never runs → the modal freezes (primary + Cancel
 * disabled, scrim/Esc dismissal blocked) until a full page reload. Racing a timer
 * guarantees the await resolves so the handler can recover.
 *
 * The write may STILL have committed server-side (data-freshness doctrine: the write
 * is the source of truth), so a timeout should surface a SOFT "couldn't confirm —
 * check the board" toast, never a hard "action failed" — realtime / the next
 * reconcile brings the true row back. The raced-out promise is left with no
 * continuation attached, so a late resolution can't mutate state.
 */
export class TimeoutError extends Error {
  constructor(ms) {
    super(`Operation timed out after ${ms}ms`);
    this.name = 'TimeoutError';
    this.isTimeout = true;
  }
}

/** Generous default — a single-row skip/reschedule is sub-second; a live-but-slow
 *  write on a bad connection still finishes well under this. Anything past it on a
 *  live socket is effectively dead. */
export const RPC_MUTATION_TIMEOUT_MS = 30000;

export function withTimeout(promise, ms = RPC_MUTATION_TIMEOUT_MS) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(ms)), ms);
  });
  // Promise.resolve adopts the supabase builder (a thenable) into a real Promise so
  // .finally is safe and the query still executes.
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => clearTimeout(timer));
}

export function isTimeoutError(err) {
  return Boolean(err && err.isTimeout);
}
